
'use client';

import Link from 'next/link';
import { AuthButton } from './auth-button';
import { Button } from './ui/button';
import { PlusCircle } from 'lucide-react';
import { useAuth } from './providers/auth-provider';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Menu, User, LogOut } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';


export function Header() {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  const pathname = usePathname();

  const NavLink = ({ href, children }: { href: string, children: React.ReactNode }) => {
    const isActive = pathname === href;
    return (
      <Link
        href={href}
        className={cn(
          "relative transition-colors whitespace-nowrap",
          isActive ? 'text-foreground' : 'text-foreground/60 hover:text-foreground/80',
          "after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:bg-primary after:transition-all after:duration-300",
          isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
        )}
      >
        {children}
      </Link>
    )
  }

  const navLinks = (
    <>
      <NavLink href="/discover">Discover</NavLink>
      <NavLink href="/artists">Artists</NavLink>
      {!loading && user && (
         <NavLink href="/my-collection">My Collection</NavLink>
      )}
      <NavLink href="/about">About</NavLink>
    </>
  );


  if (isMobile) {
      return (
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container flex h-16 items-center justify-center">
                 <Link href="/" className="flex items-center gap-2">
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 256 256"
                    className="h-6 w-6"
                    >
                    <rect width="256" height="256" fill="none"></rect>
                    <path
                        d="M88,134.5,135.8,96,224,160,168,224Z"
                        opacity="0.2"
                    ></path>
                    <path
                        d="M168,224,88,134.5,135.8,96,224,160"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="16"
                    ></path>
                    <path
                        d="M32,224a8,8,0,0,1-8-8V40a8,8,0,0,1,8-8H224a8,8,0,0,1,8,8V160"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="16"
                    ></path>
                    <circle cx="92" cy="84" r="12"></circle>
                    </svg>
                    <span className="font-bold font-headline text-lg">Art Vault</span>
                </Link>
            </div>
        </header>
      )
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center">
        <div className="flex-1 flex justify-start">
          <Link href="/" className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 256 256"
              className="h-6 w-6"
            >
              <rect width="256" height="256" fill="none"></rect>
              <path
                d="M88,134.5,135.8,96,224,160,168,224Z"
                opacity="0.2"
              ></path>
              <path
                d="M168,224,88,134.5,135.8,96,224,160"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="16"
              ></path>
              <path
                d="M32,224a8,8,0,0,1-8-8V40a8,8,0,0,1,8-8H224a8,8,0,0,1,8,8V160"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="16"
              ></path>
              <circle cx="92" cy="84" r="12"></circle>
            </svg>
            <span className="font-bold font-headline text-lg">Art Vault</span>
          </Link>
        </div>

        
        <nav className="flex-1 flex justify-center">
            <div className="hidden md:flex items-center space-x-6 text-sm font-medium">
              {navLinks}
            </div>
        </nav>
        

        <div className="flex-1 flex items-center justify-end gap-2">
          {!loading && user && (
            <Button asChild>
              <Link href="/artworks/new">
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Artwork
              </Link>
            </Button>
          )}
          <AuthButton />
        </div>
      </div>
    </header>
  );
}
